import { PharmacyStockItem, PickedDrug, PosologySuggestion } from './PharmacyStockPicker';
import { buildMealInstructionFields } from './mealInstructionCell';
import { prescriptionFinishDate } from './prescriptionDuration';

/**
 * SHER-567 slice 1 — the two translations between the prescription-history endpoint and the
 * prescription form.
 *
 * Kept out of SmartFields/index.tsx on purpose: that file is 1198 lines and already over the
 * thermo-nuclear bar, so it may gain the button and the panel's mount and nothing else.
 */

/**
 * The field type this whole module is about. Exported so the picker's visibility check and the
 * row builder below cannot drift onto different spellings of the same literal.
 */
export const TREATMENT_PRESCRIPTION_TYPE = 'treatment_prescription';

/**
 * The treatment_prescription row's field names, shared between toPrescriptionRows below and
 * SingleTreatmentSelector's manual saveDrug() so the two paths cannot diverge on a typo'd key —
 * the fields themselves are still built independently by each path, because they differ for one
 * real reason: a manually typed row computes treatment-finish from the entered duration, and a
 * picked row leaves it null (see toPrescriptionRows).
 */
export const DRUG_CODE_KEY = 'drug-code';
export const DOSE_KEY = 'treatment-dose';
export const FREQUENCY_KEY = 'treatment-frecuency';
export const START_KEY = 'treatment-start';
export const FINISH_KEY = 'treatment-finish';

/** What GET /:uuidInvestigation/prescription-history returns, narrowed to what we read. */
export interface PrescriptionHistoryItemResponse {
    name: string;
    /** What the prescription row's `drug-code` must carry for this drug — see toPrescriptionRows. */
    drugCode: string;
    /** This calling researcher's own dose+frequency history for the drug — see PosologySuggestion. */
    posologies?: PosologySuggestion[];
    /**
     * Times this researcher has prescribed the drug, summed across all its posologies. Used
     * only to sort the list (most-prescribed first) — never displayed. No separate
     * "favourites" list exists: the field norm (PCC eRx) auto-seeds and surfaces favourites
     * from prescribing history inside the same search rather than as a curated list, so this
     * reorders the one list instead of adding a second one.
     */
    usageCount?: number;
    /**
     * The pharmacy's own row for this drug, when the investigation has a pharmacy AND it
     * carries the drug. Absent is the ordinary case, not an error: clinicians prescribe off
     * the formulary, and an investigation may have no pharmacy at all (SHER-869).
     */
    pharmacyItem?: {
        id: number;
        /** Units the ward holds today. 0 means out of stock — marked, never blocked. */
        amount?: number;
    };
}

/**
 * Whether the "Fréquemment prescrits" button and panel have anywhere to point to.
 *
 * Pulled out of index.tsx so the button's own visibility check and the panel's mount
 * check cannot drift from each other — both call this, instead of each repeating the
 * same two conditions inline.
 *
 * `isEditingRow` hides the picker while an existing row is being edited. A bulk confirm makes
 * no sense mid-edit (which single row would it apply to?), and this keeps that case
 * structurally impossible — no button, no panel — rather than something a callback has to
 * detect and reject at runtime.
 *
 * SHER-869 replaced the `pharmacyId` condition with the investigation's uuid. The panel lists
 * what this clinician prescribes, which exists whether or not the investigation runs a
 * pharmacy — gating on a pharmacy hid the shortcut from exactly the tenants whose prescribing
 * is entirely off-formulary. The uuid is still required, because it is what the list is asked
 * for: with no investigation resolved there is nothing to point the panel at.
 */
export function shouldShowPharmacyStockPicker(fieldType: string, uuidInvestigation: string | null | undefined, isEditingRow: boolean = false): boolean {
    return fieldType === TREATMENT_PRESCRIPTION_TYPE && !!uuidInvestigation && !isEditingRow;
}

/**
 * Prescribing is not dispensing: a drug with nothing on the shelf is still prescribable, so
 * the pharmacy's `amount` decides how the row is *marked*, not whether it is offered. A drug
 * that arrives with no pharmacy item, or with no `amount` on it, counts as in stock — neither
 * absence is evidence of a stock-out, and marking them would put a warning on every row of a
 * pharmacy that does not track quantities, and on every drug prescribed off the formulary.
 *
 * `key` is the drug's identity in the panel, and it is the name rather than an id because the
 * server aggregates the history by normalized drug name — a drug the pharmacy does not carry
 * has no id to be identified by.
 */
export function toStockItems(items: PrescriptionHistoryItemResponse[]): PharmacyStockItem[] {
    return items
        .filter((item) => (item?.name ?? '').trim() !== '')
        .map((item) => ({
            key: item.name,
            name: item.name,
            drugCode: item.drugCode,
            inStock: item.pharmacyItem?.amount === undefined || item.pharmacyItem.amount === null || item.pharmacyItem.amount > 0,
            posologies: item.posologies,
            usageCount: item.usageCount,
        }));
}

/**
 * One prescription row per picked drug, in the shape SingleTreatmentSelector already builds
 * (see its `treatmentData` literal) so everything downstream — the table, the submit, the PDF —
 * is untouched.
 *
 * `treatment-finish` comes from the duration chosen in the panel, through the same
 * `prescriptionFinishDate` the manual sub-form uses. It is null only for a treatment the
 * clinician declared chronic — the majority case (71% of CHOM's own prescription rows carry no
 * end date) but one that still has to be said rather than assumed. Writing null unconditionally
 * is what made every picked row read as "chronique / à long terme" (SHER-834).
 *
 * `treatment-start` is a Date because that is what the existing prescription path passes; only
 * treatment_inpatient stores the bare "YYYY-MM-DD" string (SHER-260), and this is not that.
 */
/**
 * The rows are handed to SingleTreatmentSelector's persistTreatment(), which stamps
 * uuidResearcher and decides the SHER-383 persist routing — so neither belongs here.
 */
export function toPrescriptionRows(
    picked: PickedDrug[],
    now: Date = new Date(),
): Record<string, any>[] {
    // The picker doesn't collect a meal instruction yet, so every row gets the same
    // "none chosen" shape buildMealInstructionFields already produces for that case.
    const mealInstructionFields = buildMealInstructionFields(undefined);
    return picked.map((drug) => ({
        [TREATMENT_PRESCRIPTION_TYPE]: drug.name,
        // String, matching every other producer of this field (DrugType.id is string, and the
        // manual form assigns it straight through): FillSurvey.treatments.ts's
        // isSameTreatmentRow compares drug-code with strict equality in its no-postId fallback,
        // so a numeric id here would silently fail to match a persisted (always-string) row.
        // The server decides WHICH code (the PharmacyItem id when the pharmacy carries the
        // drug, else one this clinician already prescribed it under) — see SHER-869.
        [DRUG_CODE_KEY]: drug.drugCode,
        [DOSE_KEY]: drug.dose,
        [FREQUENCY_KEY]: drug.frequency,
        [START_KEY]: now,
        [FINISH_KEY]: prescriptionFinishDate(now, drug.duration),
        ...mealInstructionFields,
    }));
}
